/**
 * Pure fresh-load view resolution (kept free of tRPC/store imports so it unit-tests in isolation,
 * like `restore-stored-view.ts`).
 */

export const BUILTIN_VIEW_ID = '__builtin__';

/** The slice of a saved-view row the resolver reads. */
export interface ResolvableViewRow {
    id: bigint;
    isDefault: boolean;
    isOwner: boolean;
}

/** The slice of a filter the resolver reads (presence only). */
interface ResolvableFilter {
    id: string;
}

/**
 * The active view a fresh load should resolve to, from the saved views alone: an explicit shared
 * filter URL wins (no active view → built-in "All"); else the requested `?view` (a saved view id or
 * one of `builtInViewIds`, e.g. the fleet claims page's `__open__`); else the owner's default, then
 * any shared default; else the page's default built-in (e.g. obligations' "Latest version"); else
 * built-in "All". Pure in `rows`, so it can run synchronously while rendering (warm cache) for an
 * instant highlight, and again in the init effect (which also applies the view's filters once the
 * config is ready). Returns only the id — the caller looks up the row.
 */
export function resolveActiveViewIdFromRows(
    rows: readonly ResolvableViewRow[],
    initialActiveViewId: string | undefined,
    initialFilters: readonly ResolvableFilter[] | undefined,
    defaultBuiltInViewId: string | undefined,
    builtInViewIds?: ReadonlySet<string>,
): string {
    if ((initialFilters?.length ?? 0) > 0) return BUILTIN_VIEW_ID;
    if (initialActiveViewId && builtInViewIds?.has(initialActiveViewId)) return initialActiveViewId;
    const requested = initialActiveViewId ? rows.find((r) => String(r.id) === initialActiveViewId) : undefined;
    const fallback =
        rows.find((r) => r.isDefault && r.isOwner) ?? rows.find((r) => r.isDefault && !r.isOwner) ?? undefined;
    const target = requested ?? fallback;
    return target ? String(target.id) : (defaultBuiltInViewId ?? BUILTIN_VIEW_ID);
}
